import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Store } from "@ngxs/store";
import { AuthApiService } from "@services/auth-api/auth-api.service";
import { HttpHelperService } from "@services/http-helper.service";
import { UserResponse } from "@shared/interfaces/user.model";
import { SET_CONCURRENT_LOGIN_ERROR } from "@store/login/login.action";
import { NAVIGATE_LOGIN_PAGE } from "@store/user/user.action";
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
  timeout,
} from "rxjs";
import { environment } from "src/environments/environment";

type QueryParamsMap = Record<string, string | null>;
type PersistedSession = {
  user: Partial<UserResponse>;
  queryParams: QueryParamsMap;
};
type ServerTimeResponse = {
  epochMillis: number;
};
type RefreshErrorLike = {
  status?: number | string;
  error?: {
    info?: string;
    isForcedLogout?: boolean | string;
    localizedMessage?: string;
    message?: string;
  };
};
/**
 * Messages exchanged on the BroadcastChannel to coordinate token refresh
 * across multiple open tabs. A tab never receives its own posted message
 * (per the BroadcastChannel spec), so the sending tab is always the owner.
 */
type TabRefreshMessage =
  | { type: "REFRESH_STARTED" }
  | { type: "REFRESH_DONE" }
  | { type: "REFRESH_FAILED" };

/**
 * Central coordinator for frontend auth session lifecycle.
 *
 * Process flow:
 * 1. `setSession()` is called after a successful login/bootstrap and persists
 *    safe user metadata plus query params in local storage.
 * 2. `armRefreshTimer()` reads the `TOKEN_EXPIRY` cookie and schedules a
 *    refresh 30 seconds before expiry.
 * 3. `refreshSession()` keeps the cookie-backed session alive without exposing
 *    raw tokens to JavaScript.
 * 4. On reload, `restorePersistedSession()` rebuilds FE state from persisted
 *    metadata if the cookie expiry is still valid.
 * 5. On expiry or cross-tab session removal, `clearSession()` clears the
 *    in-memory state and refresh timer only. Persisted session metadata is
 *    removed solely on an explicit logout (via `removePersistedSession()`), so a
 *    transient teardown (e.g. a wrong device clock) cannot wipe it.
 *
 * Cross-tab refresh coordination (BroadcastChannel):
 * When multiple tabs are open, only the first tab whose timer fires sends the
 * `/auth/refresh` request. It broadcasts REFRESH_STARTED so all other tabs
 * cancel their timers. On REFRESH_DONE, all tabs re-arm their timers from the
 * updated TOKEN_EXPIRY cookie. This prevents N simultaneous refresh requests
 * and is safe against future refresh-token rotation on the BFF.
 *
 * This facade intentionally owns browser/session mechanics. Application-facing
 * auth state still lives in the NGXS user store.
 */
@Injectable({ providedIn: "root" })
export class AuthSessionFacade {
  private static readonly STORAGE_KEY = "sat.auth.session.v1";
  private static readonly TOKEN_EXPIRY_COOKIE = "TOKEN_EXPIRY";
  private static readonly REFRESH_BUFFER_MS =
    environment.authRefreshBufferMs ?? 30_000;
  private static readonly CHANNEL_NAME = "sat.auth.refresh.v1";
  // Bound the blocking time-sync so a slow/hung endpoint can never stall bootstrap.
  private static readonly SERVER_TIME_TIMEOUT_MS = 3000;
  // Plausible epoch-millis window (2020-01-01 .. 2100-01-01) used to reject a
  // malformed server time (e.g. seconds instead of millis -> ~1000x skew).
  private static readonly MIN_PLAUSIBLE_EPOCH_MS = 1_577_836_800_000;
  private static readonly MAX_PLAUSIBLE_EPOCH_MS = 4_102_444_800_000;

  private readonly userSubject$ =
    new BehaviorSubject<Partial<UserResponse> | null>(null);
  private readonly queryParamsSubject$ = new BehaviorSubject<QueryParamsMap>(
    {},
  );
  private readonly policyMenuSubject$ = new BehaviorSubject<any | null>(null);
  private readonly rawHttp: HttpClient;
  private refreshTimerId: number | null = null;
  private refreshRequest$: Observable<boolean> | null = null;
  private browserHooksRegistered = false;
  /**
   * Channel used to broadcast refresh lifecycle events to other open tabs.
   * Null when running in SSR or a browser that does not support BroadcastChannel.
   */
  private readonly tabChannel: BroadcastChannel | null;
  /**
   * Set to true when a REFRESH_STARTED message arrives from another tab.
   * Prevents this tab from firing its own refresh request for the same cycle.
   */
  private crossTabRefreshInProgress = false;
  private clockOffsetMs = 0;
  /**
   * True once syncServerTime() has applied a plausible server-time offset. Until
   * then, expiry checks fall back to the device clock, so we must not delete a
   * persisted session merely for looking expired (the device clock may be wrong).
   */
  private serverTimeSynced = false;

  /** Current user metadata used to rehydrate the app after login or reload. */
  public readonly user$ = this.userSubject$
    .asObservable()
    .pipe(map((user) => user ?? this.getFallbackUser()));
  /** Query parameters that need to survive reload for the current session. */
  public readonly queryParams$ = this.queryParamsSubject$.asObservable();
  /** Convenience stream for the policy management menu entry. */
  public readonly policyMenu$ = this.policyMenuSubject$.asObservable();
  /** Convenience stream for feature consumers that only need the menu list. */
  public readonly menuList$ = this.user$.pipe(
    map((user) => user.menuList ?? []),
  );

  constructor(
    private readonly authApiService: AuthApiService,
    private readonly httpHelperService: HttpHelperService,
    private readonly injector: Injector,
    httpBackend: HttpBackend,
  ) {
    this.rawHttp = new HttpClient(httpBackend);
    this.tabChannel =
      typeof window !== "undefined" && "BroadcastChannel" in window
        ? new BroadcastChannel(AuthSessionFacade.CHANNEL_NAME)
        : null;
    // Session restore is deferred to the APP_INITIALIZER (which runs it AFTER
    // syncServerTime), so the reload-time expiry check uses server-corrected
    // time. Restoring here would run before the offset is known and could delete
    // the persisted session on a skewed device clock.
    this.registerBrowserLifecycleHooks();
    this.registerTabChannel();
  }

  public get userSnapshot(): Partial<UserResponse> | null {
    return this.userSubject$.value;
  }

  public get queryParamsSnapshot(): QueryParamsMap {
    return this.queryParamsSubject$.value;
  }

  /**
   * Returns whether FE currently has a usable authenticated session.
   *
   * If in-memory state is empty, it attempts reload restoration from persisted
   * metadata plus the `TOKEN_EXPIRY` cookie.
   */
  public hasSession(): boolean {
    if (!this.userSubject$.value?.userId) {
      return this.restorePersistedSession();
    }

    if (this.hasUsableTokenExpiry()) {
      return true;
    }

    this.clearSession();
    return false;
  }

  /** Clears all FE-managed session state without touching server-side cookies. */
  public clearSession(): void {
    this.cancelRefreshTimer();
    this.userSubject$.next(null);
    this.queryParamsSubject$.next({});
    this.policyMenuSubject$.next(null);
  }

  /**
   * Stores safe session metadata after login/bootstrap and arms the next refresh.
   */
  public setSession(
    user: Partial<UserResponse>,
    queryParams: QueryParamsMap,
  ): void {
    const normalizedParams = this.normalizeQueryParams(queryParams);
    this.userSubject$.next(user);
    this.queryParamsSubject$.next(normalizedParams);
    this.policyMenuSubject$.next(this.extractPolicyManagementMenu(user));
    this.persistSession({ user, queryParams: normalizedParams });
    this.armRefreshTimer();
  }

  /**
   * Reload path: rebuilds FE session metadata from local storage if the
   * browser cookie still has a valid expiry timestamp.
   */
  public restorePersistedSession(): boolean {
    if (this.userSubject$.value?.userId) {
      return true;
    }

    const session = this.readPersistedSession();
    const tokenExpiry = this.readTokenExpiryTimestamp();
    if (!session || !tokenExpiry) {
      this.removePersistedSession();
      return false;
    }

    if (tokenExpiry <= this.now()) {
      // A wrong device clock can make a still-valid token look expired. Until
      // server time is confirmed (e.g. a cross-tab `storage` event firing during
      // bootstrap, before syncServerTime() has set the offset), do NOT delete the
      // persisted session on that basis — the blocking initializer re-runs this
      // after the sync, with corrected time.
      if (!this.serverTimeSynced) {
        return false;
      }
      this.removePersistedSession();
      return false;
    }

    this.userSubject$.next(session.user);
    this.queryParamsSubject$.next(session.queryParams);
    this.policyMenuSubject$.next(
      this.extractPolicyManagementMenu(session.user),
    );
    this.armRefreshTimer();
    return true;
  }

  /** Checks whether the current token is already inside the refresh window. */
  public shouldRefreshSoon(
    bufferMs = AuthSessionFacade.REFRESH_BUFFER_MS,
  ): boolean {
    const tokenExpiry = this.readTokenExpiryTimestamp();
    if (!tokenExpiry) {
      return false;
    }
    return this.now() >= tokenExpiry - bufferMs;
  }

  /**
   * Refreshes the cookie-backed session. Reuses the in-flight refresh request
   * to avoid duplicate `/auth/refresh` calls within the same tab. Across tabs,
   * a BroadcastChannel message from the owning tab suppresses all listener tabs
   * for the same refresh cycle.
   */
  public refreshSession(force = false): Observable<boolean> {
    if (!this.hasSession()) {
      return of(false);
    }

    if (!force && !this.shouldRefreshSoon()) {
      this.armRefreshTimer();
      return of(true);
    }

    // Another call within this tab already has an in-flight request; share it.
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    // Another tab has already claimed this refresh cycle; stand down.
    if (this.crossTabRefreshInProgress) {
      return of(true);
    }

    // Notify other tabs that this tab is the refresh owner for this cycle.
    this.tabChannel?.postMessage({ type: "REFRESH_STARTED" });

    const refreshUrl = this.buildAuthSessionUrl("/auth/refresh");
    this.refreshRequest$ = this.rawHttp
      .post<{ success?: boolean }>(refreshUrl, {}, { withCredentials: true })
      .pipe(
        map((response) => response?.success !== false),
        tap((success) => {
          if (success) {
            this.tabChannel?.postMessage({ type: "REFRESH_DONE" });
            this.armRefreshTimer();
            return;
          }
          this.tabChannel?.postMessage({ type: "REFRESH_FAILED" });
          this.cancelRefreshTimer();
        }),
        catchError((err) => {
          this.tabChannel?.postMessage({ type: "REFRESH_FAILED" });
          this.cancelRefreshTimer();
          this.handleRefreshForcedLogout(err);
          return of(false);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  /** Calls the BFF logout endpoint so server-side cookies can be cleared. */
  public logoutSession(): Observable<boolean> {
    if (!this.hasSession()) {
      return of(true);
    }

    const logoutUrl = this.buildAuthSessionUrl("/auth/logout");
    return this.rawHttp.post(logoutUrl, {}, { withCredentials: true }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * Syncs the local clock with the server time to handle client clock skew.
   * Never rejects so it can be used as a blocking APP_INITIALIZER without
   * stalling bootstrap if the endpoint is slow/hung.
   */
  public syncServerTime(): Observable<void> {
    const baseUrl = this.httpHelperService.resolveBackendBaseUrl();
    if (!baseUrl) {
      return of(void 0);
    }

    const serverTimeUrl = `${baseUrl}/time`;
    // HttpClient's response generic does not flow through the rxjs operators
    // under the project's tsconfig moduleResolution ("node"), so type the stream
    // explicitly to keep `response` as ServerTimeResponse instead of unknown.
    const serverTime$ = this.rawHttp.get<ServerTimeResponse>(
      serverTimeUrl,
    ) as Observable<ServerTimeResponse | null>;

    return serverTime$
      .pipe(
        timeout(AuthSessionFacade.SERVER_TIME_TIMEOUT_MS),
        tap((response) => {
          const serverEpochMs = response?.epochMillis;
          if (this.isPlausibleEpochMs(serverEpochMs)) {
            this.clockOffsetMs = serverEpochMs - Date.now();
            this.serverTimeSynced = true;
          }
        }),
        map(() => void 0),
        catchError(() => {
          // Never reject: a failed/slow/hung time sync must not block bootstrap
          // (this runs as a blocking APP_INITIALIZER, capped by the timeout
          // above). The offset is intentionally not persisted, so on failure
          // now() falls back to the device clock (clockOffsetMs stays 0 on a
          // fresh load, or the value synced earlier in this session).
          console.warn(
            '[AuthSessionFacade] server time sync failed; falling back to device time',
          );
          return of(void 0);
        }),
      );
  }

  /**
   * Token-based bootstrap entry point used by the route resolver to authenticate
   * and immediately hydrate the facade session state. Always calls the real
   * `/auth/authenticate` endpoint; mock routing is handled transparently by
   * `HttpHelperService.resolveBackendBaseUrl()`.
   */
  public authenticateAndHydrate(
    token: string,
    queryParams: QueryParamsMap,
  ): Observable<Partial<UserResponse>> {
    return this.authApiService.getUser(token).pipe(
      tap((res) => {
        if (res?.error) {
          throw new Error("Authentication failed");
        }
        this.setSession(res ?? {}, queryParams);
      }),
    );
  }

  private extractPolicyManagementMenu(user: Partial<UserResponse>): any | null {
    const menuList = user?.menuList ?? [];
    const matched = menuList.find(
      (menu: any) =>
        menu.menuId === "SA_MNPOLICIES" || menu.menuId === "MNPOLICIES",
    );
    return matched ?? null;
  }

  private get store(): Store {
    return this.injector.get(Store);
  }

  private handleRefreshForcedLogout(err: unknown): void {
    if (!this.isForcedLogoutRefreshError(err)) {
      return;
    }

    const normalizedError = err as RefreshErrorLike;
    this.clearSession();
    this.store.dispatch([
      new SET_CONCURRENT_LOGIN_ERROR({
        info: this.getForcedLogoutInfo(normalizedError.error),
        message: this.getForcedLogoutMessage(normalizedError.error),
      }),
      new NAVIGATE_LOGIN_PAGE(),
    ]);
  }

  private isForcedLogoutRefreshError(err: unknown): boolean {
    if (!err || typeof err !== "object" || Array.isArray(err)) {
      return false;
    }

    const normalizedError = err as RefreshErrorLike;
    return (
      [401, "401"].includes(normalizedError.status ?? "") &&
      (normalizedError.error?.isForcedLogout === true ||
        normalizedError.error?.isForcedLogout === "true")
    );
  }

  private getForcedLogoutInfo(errorBody?: RefreshErrorLike["error"]): string {
    return typeof errorBody?.info === "string" &&
      errorBody.info.trim().length > 0
      ? errorBody.info
      : "Concurrent login detected on this account";
  }

  private getForcedLogoutMessage(
    errorBody?: RefreshErrorLike["error"],
  ): string {
    if (
      typeof errorBody?.localizedMessage === "string" &&
      errorBody.localizedMessage.trim().length > 0
    ) {
      return errorBody.localizedMessage;
    }

    if (
      typeof errorBody?.message === "string" &&
      errorBody.message.trim().length > 0
    ) {
      return errorBody.message;
    }

    return (
      "For security reasons, you have been logged out of your current session. " +
      "Please re-login to continue or contact IT Helpdesk for assistance."
    );
  }

  private normalizeQueryParams(queryParams: QueryParamsMap): QueryParamsMap {
    const normalized = { ...queryParams };
    if (normalized["cnoteType"] === "NW") {
      normalized["cnoteType"] = "NWOO/RN";
    }
    return normalized;
  }

  private armRefreshTimer(): void {
    this.cancelRefreshTimer();

    if (
      typeof window === "undefined" ||
      !this.hasSession() ||
      !this.isDocumentVisible()
    ) {
      return;
    }

    const tokenExpiry = this.readTokenExpiryTimestamp();
    if (!tokenExpiry) {
      return;
    }

    const delay = Math.max(
      tokenExpiry - AuthSessionFacade.REFRESH_BUFFER_MS - this.now(),
      0,
    );

    this.refreshTimerId = window.setTimeout(() => {
      // eslint-disable-next-line custom-rules/enforce-take-until-destroyed-on-subscribe -- one-shot refresh HTTP observable owned by a root service; no component lifecycle to bind here
      this.refreshSession(true).subscribe();
    }, delay);
  }

  private cancelRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      window.clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private registerBrowserLifecycleHooks(): void {
    if (this.browserHooksRegistered || typeof window === "undefined") {
      return;
    }

    this.browserHooksRegistered = true;

    window.addEventListener("focus", () => this.handleBrowserResume());
    document.addEventListener("visibilitychange", () => {
      if (this.isDocumentVisible()) {
        this.handleBrowserResume();
        return;
      }
      this.cancelRefreshTimer();
    });
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key !== AuthSessionFacade.STORAGE_KEY) {
        return;
      }

      if (!event.newValue) {
        this.clearSession();
        return;
      }

      if (this.userSubject$.value?.userId) {
        return;
      }

      this.restorePersistedSession();
    });
  }

  private handleBrowserResume(): void {
    // The device clock can jump while a tab is backgrounded (laptop sleep/wake,
    // manual clock change), which is exactly when a stale offset would cause a
    // false logout. Re-confirm the server-time offset before re-evaluating the
    // token expiry.
    // eslint-disable-next-line custom-rules/enforce-take-until-destroyed-on-subscribe -- one-shot time-sync HTTP observable owned by a root service; no component lifecycle to bind here
    this.syncServerTime().subscribe(() => this.evaluateSessionAfterResume());
  }

  private evaluateSessionAfterResume(): void {
    if (!this.hasSession()) {
      return;
    }

    if (!this.hasUsableTokenExpiry()) {
      this.clearSession();
      return;
    }

    if (this.shouldRefreshSoon()) {
      // eslint-disable-next-line custom-rules/enforce-take-until-destroyed-on-subscribe -- one-shot refresh HTTP observable owned by a root service; no component lifecycle to bind here
      this.refreshSession(true).subscribe();
      return;
    }

    this.armRefreshTimer();
  }

  private persistSession(session: PersistedSession): void {
    try {
      localStorage.setItem(
        AuthSessionFacade.STORAGE_KEY,
        JSON.stringify(session),
      );
    } catch {
      // Storage failures should not block authentication.
    }
  }

  private readPersistedSession(): PersistedSession | null {
    try {
      const raw = localStorage.getItem(AuthSessionFacade.STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as PersistedSession;
      if (!parsed?.user?.userId) {
        return null;
      }

      return {
        user: parsed.user,
        queryParams: parsed.queryParams ?? {},
      };
    } catch {
      return null;
    }
  }

  public removePersistedSession(): void {
    try {
      localStorage.removeItem(AuthSessionFacade.STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
  }

  private readTokenExpiryTimestamp(): number | null {
    if (typeof document === "undefined") {
      return null;
    }

    const cookieValue = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .find((entry) =>
        entry.startsWith(`${AuthSessionFacade.TOKEN_EXPIRY_COOKIE}=`),
      )
      ?.split("=")[1];

    if (!cookieValue) {
      return null;
    }

    const timestamp = Number(cookieValue);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private hasUsableTokenExpiry(): boolean {
    const tokenExpiry = this.readTokenExpiryTimestamp();
    return tokenExpiry !== null && tokenExpiry > this.now();
  }

  private isPlausibleEpochMs(value: number | undefined): value is number {
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value >= AuthSessionFacade.MIN_PLAUSIBLE_EPOCH_MS &&
      value <= AuthSessionFacade.MAX_PLAUSIBLE_EPOCH_MS
    );
  }

  private now(): number {
    return Date.now() + this.clockOffsetMs;
  }

  private isDocumentVisible(): boolean {
    return (
      typeof document === "undefined" || document.visibilityState !== "hidden"
    );
  }

  private getFallbackUser(): Partial<UserResponse> {
    return {
      name: "XXX",
      preferName: "XXX",
      lastLoginDate: "XX/XX/XXXX XX:XX:XX",
      menuAccess: [],
      menuList: [],
    };
  }

  // Refresh and logout requests must target the same origin as the login
  // cookie. Delegating to HttpHelperService.resolveBackendBaseUrl() keeps this
  // in sync with the rest of the app without duplicating environment checks.
  private buildAuthSessionUrl(path: string): string {
    return `${this.httpHelperService.resolveBackendBaseUrl()}${path}`;
  }

  /**
   * Wires up the BroadcastChannel message handler.
   * No-op when the channel is unavailable (SSR / unsupported browser).
   */
  private registerTabChannel(): void {
    if (!this.tabChannel) {
      return;
    }

    this.tabChannel.onmessage = (event: MessageEvent<TabRefreshMessage>) => {
      this.handleTabMessage(event.data);
    };
  }

  /**
   * Handles an incoming BroadcastChannel message from another tab.
   *
   * REFRESH_STARTED — another tab is the refresh owner for this cycle;
   *   cancel our timer so we don't send a duplicate request.
   *
   * REFRESH_DONE — the owning tab refreshed successfully; the browser has
   *   already updated the TOKEN_EXPIRY cookie, so we just re-arm our timer.
   *
   * REFRESH_FAILED — the owning tab could not refresh; check whether the
   *   cookie is still valid (it may not have expired yet) and either re-arm
   *   or clear the session.
   */
  private handleTabMessage(msg: TabRefreshMessage): void {
    switch (msg.type) {
      case "REFRESH_STARTED":
        this.crossTabRefreshInProgress = true;
        this.cancelRefreshTimer();
        break;

      case "REFRESH_DONE":
        this.crossTabRefreshInProgress = false;
        // TOKEN_EXPIRY cookie updated by the server; re-read it for our timer.
        this.armRefreshTimer();
        break;

      case "REFRESH_FAILED":
        this.crossTabRefreshInProgress = false;
        if (this.hasUsableTokenExpiry()) {
          // Token hasn't expired yet — stay alive and retry at the normal time.
          this.armRefreshTimer();
        } else {
          this.clearSession();
        }
        break;
    }
  }
}
